'use client';

import React, { PropsWithChildren } from 'react';
import { cva } from 'class-variance-authority';
import { type VariantProps } from 'class-variance-authority';

const getClassName = cva(
	'flex flex-row gap-100 items-start justify-start text-start text-caption w-full',
	{
		variants: {
			intent: {
				success: [],
				warning: [],
				error: [],
				info: [],
			},
			kind: {
				outline: ['border'],
				filled: [],
				subtle: [],
				text: [],
			},
			hasIcon: {
				true: [],
				false: [],
			},
			hasOnClick: {
				true: [],
				false: [],
			},
			inverse: {
				true: [],
				false: [],
			},
		},

		compoundVariants: [
			{
				kind: ['outline', 'filled', 'subtle'],
				className: ['rounded-[5px]', 'p-100'],
			},

			// intent: ERROR
			{
				intent: ['error'],
				kind: ['outline'],
				className: ['border-error-border'],
			},
			{
				intent: ['error'],
				kind: ['filled'],
				className: [
					'text-error-text-inverse',
					'bg-error-container',
					'[&>.icon-container]:text-error-icon-inverse',
				],
			},
			{
				intent: ['error'],
				kind: ['subtle'],
				className: ['bg-error-container-subtle'],
			},
			{
				intent: ['error'],
				kind: ['outline', 'subtle', 'text'],
				className: ['text-error-text', '[&>.icon-container]:text-error-icon'],
			},

			// intent: WARNING
			{
				intent: ['warning'],
				kind: ['outline'],
				className: ['border-warning-border'],
			},
			{
				intent: ['warning'],
				kind: ['filled'],
				className: [
					'text-warning-text-inverse',
					'bg-warning-container',
					'[&>.icon-container]:text-warning-icon-inverse',
				],
			},
			{
				intent: ['warning'],
				kind: ['subtle'],
				className: ['bg-warning-container-subtle'],
			},
			{
				intent: ['warning'],
				kind: ['outline', 'subtle', 'text'],
				className: ['text-warning-text', '[&>.icon-container]:text-warning-icon'],
			},

			// intent: SUCCESS
			{
				intent: ['success'],
				kind: ['outline'],
				className: ['border-success-border'],
			},
			{
				intent: ['success'],
				kind: ['filled'],
				className: [
					'text-success-text-inverse',
					'bg-success-container',
					'[&>.icon-container]:text-success-icon-inverse',
				],
			},
			{
				intent: ['success'],
				kind: ['subtle'],
				className: ['bg-success-container-subtle'],
			},
			{
				intent: ['success'],
				kind: ['outline', 'subtle', 'text'],
				className: ['text-success-text', '[&>.icon-container]:text-success-icon'],
			},

			// intent: INFO
			{
				intent: ['info'],
				kind: ['outline'],
				className: ['border-info-border'],
			},
			{
				intent: ['info'],
				kind: ['filled'],
				className: [
					'text-info-text-inverse',
					'bg-info-container',
					'[&>.icon-container]:text-info-icon-inverse',
				],
			},
			{
				intent: ['info'],
				kind: ['subtle'],
				className: ['bg-info-container-subtle'],
			},
			{
				intent: ['info'],
				kind: ['outline', 'subtle', 'text'],
				className: ['text-info-text', '[&>.icon-container]:text-info-icon'],
			},
		],

		defaultVariants: {
			intent: 'error',
			kind: 'filled',
			hasOnClick: false,
			inverse: false,
		},
	}
);

export interface AlertProps extends VariantProps<typeof getClassName> {
	as?: React.ElementType;
	icon?: React.ReactNode;
	inverse?: boolean;
	onClick?: () => void;
}

export default function Alert({
	as = 'div',
	intent,
	kind,
	icon,
	inverse = false,
	onClick,
	children,
}: PropsWithChildren<AlertProps>) {
	const className = getClassName({
		intent,
		kind,
		hasIcon: Boolean(icon),
		hasOnClick: Boolean(onClick),
		inverse,
	});

	return React.createElement(
		onClick && !['button', 'a'].includes(as as string) ? 'button' : as,
		{ onClick, className },
		<>
			{icon && <div className="icon-container w-200 h-200 text-[20px] leading-none">{icon}</div>}
			{children}
		</>
	);
}
